import { toast } from "sonner";
import { NavigateFunction } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Define and export the UserData interface
export interface UserData {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  role: string;
  profileImageUrl: string | null;
  connectionKey: string;
  websiteUrl: string;
  bookingCalendar: string;
  showActivity: string;
  country: string;
  language: string;
  languageSecondary: string;
  profileBio: string;
}

export const fetchUserProfile = async (navigate: NavigateFunction): Promise<UserData | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log("No active session - redirecting to auth");
      navigate('/auth');
      return null;
    }

    // First check if user has a valid role
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (roleError) {
      console.error("Error fetching user role:", roleError);
      throw new Error("Failed to verify user role");
    }

    if (!roleData || (roleData.role !== 'client' && roleData.role !== 'therapist')) {
      console.error("Invalid user role");
      navigate('/auth');
      return null;
    }

    // Then fetch the profile data
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }

    if (!profile) {
      return null;
    }

    // Transform the database profile into UserData format
    return {
      id: session.user.id,
      email: session.user.email || '',
      name: profile.company || '',
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      role: roleData.role,
      profileImageUrl: profile.profile_image_url,
      connectionKey: profile.connection_key || '',
      websiteUrl: profile.website_url || '',
      bookingCalendar: (profile as any).booking_calendar || '',
      showActivity: profile.show_activity ? 'yes' : 'no',
      country: profile.country || '',
      language: profile.language || '',
      languageSecondary: profile.language_secondary || '',
      profileBio: profile.profile_bio || ''
    };
  } catch (error) {
    console.error("Error in fetchUserProfile:", error);
    throw error;
  }
};

export const saveUserProfile = async (userData: UserData): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("No active session");
    }

    const profileData = {
      user_id: session.user.id,
      email: session.user.email,
      company: userData.name,
      first_name: userData.firstName,
      last_name: userData.lastName,
      profile_image_url: userData.profileImageUrl,
      website_url: userData.websiteUrl,
      booking_calendar: userData.bookingCalendar,
      show_activity: userData.showActivity === 'yes',
      country: userData.country,
      language: userData.language,
      language_secondary: userData.languageSecondary,
      profile_bio: userData.profileBio,
      connection_key: userData.connectionKey,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('user_profiles')
      .upsert(profileData);

    if (error) {
      console.error("Error saving profile:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in saveUserProfile:", error);
    throw error;
  }
};
